import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { showAlert } from '../../../services/alert';
import { useGetCropStagesQuery } from '../../../services/mVPGraphQlService';
import { stageLabel } from '../utils/scheduleStage';
import { StageRanges } from '../utils/scheduleLineSchema';

export type StageOption = { value: string; label: string };

type Args = {
  enabled: boolean;
  crop?: string;
  hybrid?: string;
  season?: string;
};

export const useStageOptions = ({
  enabled,
  crop,
  hybrid,
  season,
}: Args) => {
  const dispatch = useDispatch();
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (!enabled) setRequested(false);
  }, [enabled]);

  const requestStages = useCallback(
    () => setRequested(true),
    []
  );

  const { data, isFetching, isError } = useGetCropStagesQuery(
    {
      wsId,
      cropName: crop || undefined,
      hybridProductionCode: hybrid || undefined,
      season: season || undefined,
    },
    { skip: !enabled || !requested || !crop || !hybrid }
  );
  const result = data?.data?.GetCropStages;

  const { stageOptions, stageRanges } = useMemo(() => {
    const options: StageOption[] = [];
    const ranges: StageRanges = {};
    (result?.stages ?? []).forEach((item) => {
      const value = stageLabel(item.stage) || item.stageName;
      if (!value || ranges[value]) return;
      options.push({ value, label: item.stageName || value });
      ranges[value] = {
        dasFrom: item.dasFrom,
        dasTo: item.dasTo,
      };
    });
    return { stageOptions: options, stageRanges: ranges };
  }, [result]);

  useEffect(() => {
    if (!enabled || !requested) return;
    const status = result?.responseStatus;
    if (isError || (status && status.status !== 'SUCCESS')) {
      dispatch(
        showAlert({
          content:
            status?.errorMessages?.[0] ||
            'Unable to load crop stages',
          severity: 'error',
        })
      );
    }
  }, [enabled, requested, isError, result, dispatch]);

  return {
    stageOptions,
    stageRanges,
    requestStages,
    loading: isFetching,
  };
};
